import { createAsyncThunk } from '@reduxjs/toolkit';
import { SearchComputeState, setUpdateServiceStatus } from './search-compute-slice';
import { setLoadingComponent, setServicesList } from './search-compute-slice';
import { APIPrefix, buildUrl } from '../../utils/functions';
import {
  ComputeServiceType,
  createAppAsyncThunk,
  NavigateEventContext,
  Site,
} from '../../utils/types';
import { useAppSelector } from '../../app/hooks';
import { DataState } from '../../app/data-slice';
import {
  Event,
  EventType,
  InitiateComputeSearchParams,
  ServiceStatusParams,
} from '../../utils/events';

//	------------------------------------------------------------
//
//	for each site, calculate the distance from the site to the user's
//	latitude and longitude. Add the result to the list of information
//	stored about each site
//
//	------------------------------------------------------------

function _addDistances(args: {
  siteList: object[];
  userLatitude: number | undefined;
  userLongitude: number | undefined;
}) {
  // loop over sites.
  for (var i = 0; i < args.siteList.length; i++) {
    var site: {} = args.siteList[i];

    // handle both latitude and longitude provided.
    var distance: number | undefined = undefined;
    if (
      args.userLatitude !== undefined &&
      args.userLongitude !== undefined &&
      'latitude' in site &&
      'longitude' in site
    )
      try {
        var siteLat: number = Number(site['latitude']);
        var siteLong: number = Number(site['longitude']);

        // find separation in radians
        let rLat = (siteLat - args.userLatitude) * (Math.PI / 180);
        let rLong = (siteLong - args.userLongitude) * (Math.PI / 180);
        let a =
          Math.pow(Math.sin(rLat / 2), 2) +
          Math.cos(siteLat * (Math.PI / 180)) *
            Math.cos(args.userLatitude * (Math.PI / 180)) *
            Math.pow(Math.sin(rLong / 2), 2);

        // find separation on a sphere of unit radius.
        distance = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      } catch (e) {
        console.log('Error calculating distance from user to site');
      }

    // handle just latitude provided.
    if (
      args.userLatitude !== undefined &&
      args.userLongitude === undefined &&
      'latitude' in site
    )
      try {
        var siteLat: number = Number(site['latitude']);

        // find separation in latitude.
        distance = Math.abs(siteLat - args.userLatitude);
      } catch (e) {
        console.log('Error calculating distance from user to site');
      }

    // handle just longitude provided.
    if (
      args.userLongitude !== undefined &&
      args.userLatitude === undefined &&
      'longitude' in site
    )
      try {
        var siteLong: number = Number(site['longitude']);

        // find separation in longitude.
        distance = Math.abs(siteLong - args.userLongitude);
      } catch (e) {
        console.log('Error calculating distance from user to site');
      }

    // append this distance measurement to the site object.
    Object.assign(site, { distance: distance });
  }
} // addDistances

//	------------------------------------------------------------
//
//	Part of the quicksort algorithm.
//	Uses the rightmost element as the starting element
//	Rearranges the values within the array range so that all the values
//	on the left are less than the starting element and those on the right
//	are greater
//	Treats an 'undefined' range as the largest possible value
//	The items to the left and right are unsorted
//	Returns the new index of the starting element
//
//	------------------------------------------------------------

function _partition(args: { array: any[]; low: number; high: number }) {
  // choose the rightmost element as the starting element
  var pivot: number = -1;
  if ('distance' in args.array[args.high]) pivot = args.array[args.high]['distance'];
  else return args.high;

  // create a reference to before the first element
  // this represents the position we will move all smaller elements to
  var i: number = args.low - 1;

  // traverse through all the elements comparing each element with the starting element
  for (var j = args.low; j < args.high; j++)
    if ('distance' in args.array[j])
      if (args.array[j]['distance'] <= pivot) {
        // if the element we are checking is smaller than the pivot
        // swap this element with the element to the right of index i
        i++;
        let temp = args.array[i];
        args.array[i] = args.array[j];
        args.array[j] = temp;
      }

  // finally move the starting element so that it is to the immediate right of the smaller elements
  let temp = args.array[i + 1];
  args.array[i + 1] = args.array[args.high];
  args.array[args.high] = temp;

  //Return the position of the starting element
  return i + 1;
} // partition

//	------------------------------------------------------------
//
//	Uses the quicksort algorithm to sort the sites by distance
//	Calls the partition function so that it knows all the elements to
//	the left of a known position are smaller and all elements to the right
//	are larger.
//	It then recursively calls itself, once for the sub-array on the left and
//	once for the sub-array on the right.
//	This continues until the sub-arrays are length one
//
//	------------------------------------------------------------

function _sortByDistance(args: { array: any[]; low: number; high: number }) {
  // first check to see if we are done or not
  if (args.low < args.high) {
    // find a pivot element such that all smaller elements are on the left and larger on the right
    let p = _partition({ array: args.array, low: args.low, high: args.high });

    // recursive call for the smaller elements
    _sortByDistance({ array: args.array, low: args.low, high: p - 1 });

    // recursive call for the larger elements
    _sortByDistance({ array: args.array, low: p + 1, high: args.high });
  }
} // sortByDistance

//	------------------------------------------------------------
//
//	flatten the site, node, associated_services[] structure
//	so that we just have a list of services, with site and node
//	duplicated.
//
//	------------------------------------------------------------

function _flattenServices(args: { servicesList: any[]; stateData?: { sites?: any[] } }) {
  //
  const flattenedServices: ComputeServiceType[] = [];

  const tmpServices = Array.isArray(args.servicesList) ? args.servicesList.slice() : [];

  for (let siteIndex = 0; siteIndex < tmpServices.length; siteIndex++) {
    const site = tmpServices[siteIndex];
    const assoc = site?.associated_services ?? [];

    if (!Array.isArray(assoc) || assoc.length === 0) continue;

    for (let serviceIndex = 0; serviceIndex < assoc.length; serviceIndex++) {
      // Build compute service objects
      const sitesList = args.stateData?.sites ?? [];
      const siteUuidLower = (site?.site_uuid ?? '').toString().toLowerCase();
      const siteMatchIndex = sitesList.findIndex(
        (item: any) => (item.site_uuid ?? '').toString().toLowerCase() === siteUuidLower,
      );

      const assocSvc = assoc[serviceIndex];
      const computedSite =
        siteMatchIndex > -1
          ? sitesList[siteMatchIndex]
          : {
              node: (site?.node ?? '').toString().toUpperCase(),
              site: (site?.site ?? '').toString().toUpperCase(),
              site_uuid: siteUuidLower,
            };

      const sessionType = assocSvc?.type ?? '';
      const svcTypeUpper = (sessionType ?? '').toString().toUpperCase();

      const computeEntry: ComputeServiceType = {
        site: computedSite,
        compute_service_id: assocSvc?.id,
        resource_type: 'srcnet',
        session_type: sessionType,
        service_url: buildUrl({
          prefix: assocSvc?.prefix,
          host: assocSvc?.host,
          path: assocSvc?.path,
          port: assocSvc?.port,
        }),
        host_url:
          svcTypeUpper === 'CANFAR'
            ? buildUrl({
                prefix: assocSvc?.prefix,
                host: assocSvc?.host,
                path: '',
                port: assocSvc?.port,
              })
            : undefined,
        is_force_disabled: assocSvc?.is_force_disabled ?? false,
        downtime: assocSvc?.downtime ?? [],
        service_name: assocSvc?.name ?? '',
        active_status: assocSvc?.active_status ?? false,
        health_status: assocSvc?.health_status ?? 'unknown',
        top_banner: assocSvc?.top_banner ?? 'unknown status',
        bottom_banner_type: assocSvc?.bottom_banner_type ?? 'none',
        bottom_banner: assocSvc?.bottom_banner ?? '',
        active_session_count: assocSvc?.active_session_count,
        is_embedded: Boolean(
          assocSvc?.name?.toString().toUpperCase().indexOf('(EMBED)') > -1,
        ),
        compute_status: assocSvc?.active_status ? 'available' : 'unavailable',
      };

      flattenedServices.push(computeEntry);
    }
  }

  return flattenedServices;
} // _flattenServices

//	------------------------------------------------------------
//
//	An asynchronous function that resolves a name against
//	an online database, such as NED, SIMBAD or Sesame.
//
//	------------------------------------------------------------

export const loadComputeData = createAsyncThunk(
  'search_compute/loadComputeData',
  async (_, thunkAPI: any) => {
    var urlCommand: string = APIPrefix() + '/v1/site_capabilities/list_compute';
    // fetch the state.
    const stateFetch: any = thunkAPI.getState();
    const state: SearchComputeState = stateFetch.searchCompute;
    const stateData: DataState = stateFetch.data;

    try {
      var params: string = '';
      // add node.
      if (
        state.sNodeValue !== undefined &&
        state.sNodeValue !== '' &&
        state.sNodeValue !== 'all'
      ) {
        params = params + '&node=' + encodeURIComponent(state.sNodeValue);
      }

      // add site.
      if (
        state.sSiteValue !== undefined &&
        state.sSiteValue !== '' &&
        state.sSiteValue !== 'all'
      )
        params = params + '&site=' + encodeURIComponent(state.sSiteValue);

      // add description.
      if (state.sDescription !== undefined && state.sDescription !== '')
        params = params + '&description=' + encodeURIComponent(state.sDescription);

      // add hardware capabilities.
      if (state.sGPU == true) params = params + '&gpu=T';
      if (state.sLargeScratch == true) params = params + '&large_scratch=T';
      if (state.sHighMemory == true) params = params + '&high_memory=T';
      if (state.sFastScratch == true) params = params + '&fast_scratch=T';

      // add hardware type.
      if (
        state.sHardwareType !== undefined &&
        state.sHardwareType !== '' &&
        state.sHardwareType !== 'any'
      )
        params = params + '&hardware_type=' + encodeURIComponent(state.sHardwareType);

      // add service type
      if (
        state.sServiceType !== undefined &&
        state.sServiceType !== '' &&
        state.sServiceType !== 'all'
      )
        params = params + '&service_type=' + encodeURIComponent(state.sServiceType);

      // add service type
      if (
        state.sAvailabilityType !== undefined &&
        state.sAvailabilityType !== '' &&
        state.sAvailabilityType !== 'all'
      )
        params =
          params + '&availability_type=' + encodeURIComponent(state.sAvailabilityType);

      // add middleware version.
      if (state.sMiddlewareVersion !== undefined && state.sMiddlewareVersion !== '')
        params =
          params + '&middleware_version=' + encodeURIComponent(state.sMiddlewareVersion);

      if (params !== '') urlCommand = urlCommand + '?' + params.substr(1);

      try {
        const apiResult = await fetch(urlCommand, {
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (apiResult.ok) {
          const computeJson = await apiResult.json();

          // get services list.
          var servicesList: Site[] = [];
          if (computeJson !== undefined) {
            servicesList = computeJson;
          }

          // compute the distance of each site from the requested position, and sort the results by distance.
          _addDistances({
            siteList: servicesList,
            userLatitude: state.sLatitude,
            userLongitude: state.sLongitude,
          });
          _sortByDistance({ array: servicesList, low: 0, high: servicesList.length - 1 });

          // update the state with the list of returned site services.
          thunkAPI.dispatch(
            setServicesList(_flattenServices({ servicesList: servicesList, stateData })),
          );
          thunkAPI.dispatch(setLoadingComponent(false));
        }

        // HTTP 401 means the session has expired.
        if (apiResult.status === 401) {
          console.log('loadComputeData 401.');
        }
      } catch (e) {
        console.log(e);
      }
    } catch (e) {
      console.log(e);
    }
  },
); // loadComputeData

//	-------------------------------------------------
//
//	Check if we hold a supplied compute service ID
//	in the state array, and if so we should update
//	the status.
//
//	-------------------------------------------------

export const updateComputeSearchServiceStatus = createAsyncThunk(
  'search_compute/loadComputeData',
  async (serviceStatus: ServiceStatusParams, thunkAPI: any) => {
    const stateFetch: any = thunkAPI.getState();
    const state: SearchComputeState = stateFetch.searchCompute;

    console.log(
      'SSE: updateComputeStatus fires: ',
      serviceStatus.id,
      serviceStatus.activeStatus,
      state.sServicesList,
    );

    // check if this service ID exists in the local compute state array.
    const index: number = state.sServicesList.findIndex(
      (item) => item.id === serviceStatus.id,
    );
    if (index > -1) {
      //
      // shallow copy the array item.
      let updateService: any = Object.assign({}, state.sServicesList[index]);
      updateService.active_status = serviceStatus.activeStatus;
      updateService.health_status = serviceStatus.healthStatus;
      updateService.top_banner = serviceStatus.topBanner;
      updateService.bottom_banner_type = serviceStatus.bottomBannerType;
      updateService.bottom_banner = serviceStatus.bottomBanner;

      // update the state array with the new statuses.
      console.log('SSE: setting status to ' + serviceStatus.activeStatus);
      const newList = [
        ...state.sServicesList.filter((item) => item.id !== serviceStatus.id),
        updateService,
      ];

      thunkAPI.dispatch(setServicesList(newList));
      //
    }
  },
); // updateComputeSearchServiceStatus

export const eventHandler = createAppAsyncThunk(
  'search_compute/SearchComputeEventHandler',
  async (args: { event: Event; ctx?: NavigateEventContext }, thunkAPI) => {
    switch (args.event.eventType) {
      case EventType.INITIATE_COMPUTE_SEARCH:
        thunkAPI.dispatch(loadComputeData());
        break;

      case EventType.COMPUTE_SERVICE_STATUS_CHANGED:
        const params = args.event.parameters;
        thunkAPI.dispatch(setUpdateServiceStatus(params));
    }
  },
);
